import Button from "@components/ui/Button";
import ErrorBanner from "@components/shared/ErrorBanner";
import { useReactivationCampaigns } from "@hooks/useReactivationCampaigns";
import { useReactivationAnalytics } from "@hooks/useReactivationAnalytics";
import AnalyticsFilters from "@components/scheduling/analytics/AnalyticsFilters";
import StatTiles from "@components/scheduling/analytics/StatTiles";
import TrendChart from "@components/scheduling/analytics/TrendChart";
import Funnel from "@components/scheduling/analytics/Funnel";
import OutcomeBars from "@components/scheduling/analytics/OutcomeBars";
import InsightCard from "@components/scheduling/analytics/InsightCard";

/**
 * What the campaigns are doing, at a glance. The campaign list is only here
 * to fill the filter and to gate the 30 s poll; the figures come from the
 * analytics hook, whose two request states mean nothing on this tab ever
 * waits for anything else: the first load is skeletons, every later load
 * keeps the content and shows a thin bar, and the summary fails alone.
 */
export default function ReactivationOverviewTab({ clinicId }) {
  const { campaigns } = useReactivationCampaigns(clinicId);
  const a = useReactivationAnalytics(clinicId, { campaigns });
  const loading = a.numbersLoading && !a.numbers;

  return (
    <div className="space-y-4">
      <AnalyticsFilters
        campaigns={campaigns}
        filters={a.filters}
        onChange={a.setFilters}
        period={a.numbers?.period}
        startedAt={a.numbers?.started_at}
        refreshing={a.refreshing}
      />

      {a.numbersError && (
        <div className="flex items-start gap-3">
          <div className="flex-1"><ErrorBanner message={a.numbersError} /></div>
          <Button color="light" size="sm" onClick={() => a.refresh()}>Retry</Button>
        </div>
      )}

      <StatTiles numbers={a.numbers} loading={loading} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <TrendChart daily={a.numbers?.daily || []} loading={loading} />
        </div>
        <Funnel totals={a.numbers?.totals} loading={loading} />
        <OutcomeBars outcomes={a.numbers?.outcomes || []} loading={loading} />
      </div>

      <InsightCard
        insight={a.insight}
        loading={a.insightLoading && !a.insight}
        error={a.insightError}
        regenerating={a.regenerating}
        onRetry={a.retryInsight}
        onRegenerate={a.regenerate}
      />
    </div>
  );
}
